import { Box, ContentTemplate, Grid, KeyValue, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import React, { useState } from 'react';
import { MutationStatus } from 'react-query';
import FormHeader from '../../../../components/form-items/FormHeader';
import CustomFeeRow from '../../../../components/CustomFeeRow';
import PaymentButton, { PaymentMethod } from '../PaymentButton';
import { SchoolCandidatePaymentDetailsResponse } from '../../../../api-refactored/school-candidate-payment/usePostRetrieveSchoolCandidatePaymentDetails';
import { useSchoolCandidatePaymentDetails } from './logic/useSchoolCandidatePaymentDetails';
import RouteKey from '../../../../routes/key';
import PayNowProxyDetailsModal from '../../../subject/components/SubjectAmendment/PayNowProxyDetailsModal';

const useStyles = makeStyles()((theme) =>
({
  input: {
    marginBottom: theme.spacing(4),
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

interface SchoolCandidatePaymentDetailsProps {
  schoolCandidatePaymentDetails: SchoolCandidatePaymentDetailsResponse;
  organisationUuid: string;
}

const { payment } = RouteKey.child;
const { schoolCandidatePayment } = RouteKey.payment.child;
const { receipt } = schoolCandidatePayment.child;

const SchoolCandidatePaymentDetails = ({
  schoolCandidatePaymentDetails,
  organisationUuid,
}: SchoolCandidatePaymentDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);
  const [openPayNowProxyModal, setOpenPayNowProxyModal] = useState<boolean>(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const {
    handlePaymentOption,
    handleClickPaymentButtonTest,
    postPayNowProxyDetails,
    paynowProxyOnCloseCallback,
  } = useSchoolCandidatePaymentDetails({
    setPaymentAccessDetailsOption,
    schoolCandidatePaymentDetails,
    organisationUuid,
    setApiButtonStatus,
    setOpenPayNowProxyModal,
  });

  const onClosePayNowProxyModal = () => {
    if (paynowProxyOnCloseCallback) {
      paynowProxyOnCloseCallback(PaymentMethod.PAYNOW_QR);
    }
  };

  return (
    <>
      <ContentTemplate continuous isDividerBottom>
        <FormHeader title="Payment Details" />
        <Typography className={classes.input} variant="body1">
          Please check that the candidate name and registered examination(s) are correct.
        </Typography>
        <KeyValue
          id="schoolCandidatePayment-candidateName"
          keyText={t('components->>SchoolCandidatePayment.PaymentDetailsName')}
          valueText={schoolCandidatePaymentDetails.name}
        />
        <KeyValue
          id="schoolCandidatePayment-examYear"
          keyText={t('components->>SchoolCandidatePayment.PaymentDetailsExamYear')}
          valueText={schoolCandidatePaymentDetails.examYear.toString()}
        />
      </ContentTemplate>
      <ContentTemplate continuous>
        <Box>
          <CustomFeeRow
            label={t('components->>SchoolCandidatePayment.ExamLevel')}
            value={!isMobile ? t('components->>SelectedSubjectReview.Amount (SGD)') : ''}
            currencyDenomination={false}
            variant="header"
          />
          {schoolCandidatePaymentDetails.examLevelsFeeModels.map((ele) => (
            <CustomFeeRow label={ele.examLevel} value={ele.outstandingAmount} key={ele.examLevel} />
          ))}
          <CustomFeeRow
            label={t('components->>SelectedSubjectReview.TotalPayable')}
            value={schoolCandidatePaymentDetails.totalPayable}
            variant="footer"
          />
        </Box>
      </ContentTemplate>
      <ContentTemplate continuous>
        <Grid container direction="row" alignItems="flex-start" spacing={3}>
          <Grid xs={12} md={4}>
            <PaymentButton
              buttonClassName={classes.paymentButton}
              isUniqueLink={false}
              disabled={isPaymentDisabled}
              onPaymentComplete={(isRetry) => {
                setIsPaymentDisabled(isRetry);
              }}
              paymentMethodsAvailable={paymentAccessDetailsOption}
              onClickPaymentButton={(callback) => {
                handleClickPaymentButtonTest(callback);
              }}
              onHandlePaymentMethod={(callback, method, paymentModalOnClose) => {
                handlePaymentOption(callback, method, paymentModalOnClose);
              }}
              onSuccessPayment={() => {
                history.push(
                  `/${payment}/${schoolCandidatePayment.route}/${schoolCandidatePaymentDetails?.uuid}/${receipt.route}`
                );
              }}
              fullWidth
              buttonText={t('exam->>ReviewExam.ConfirmAndPay')}
              buttonVariant="contained"
              buttonErrorText={t('components->>SchoolCandidatePayment.PaymentErrorButtonLabel')}
              customPaymentMessage={t('components->>SchoolCandidatePayment.PaymentMessage')}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      <PayNowProxyDetailsModal
        modalState={openPayNowProxyModal}
        setOpenModalState={setOpenPayNowProxyModal}
        postPayNowProxyDetails={postPayNowProxyDetails}
        applicationUuid={schoolCandidatePaymentDetails.uuid}
        apiButtonStatus={apiButtonStatus}
        setApiButtonStatus={setApiButtonStatus}
        onClosePayNowProxyModal={onClosePayNowProxyModal}
      />
    </>
  );
};

export default SchoolCandidatePaymentDetails;
